import React, { lazy, Suspense } from 'react';
import './App.css';
import './mobile-optimizations.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import ErrorBoundary from './components/ErrorBoundary';
import ModernLandingPage from './components/ModernLandingPage';
import NewLandingPage from './components/NewLandingPage';
import PublicExplorePage from './components/PublicExplorePage';
import Login from './components/Login';
import AuthCallback from './components/AuthCallback';
import Dashboard from './components/Dashboard';
import BiblePage from './components/BiblePage';
import CommunityPage from './components/CommunityPage';
import SuperAdminCenter from './components/SuperAdminCenter';
import PricingPage from './components/PricingPage';
import CollabPage from './components/CollabPage';
import MockPaymentPage from './components/MockPaymentPage';
import PaymentSuccessPage from './components/PaymentSuccessPage';
import PaymentRetryBanner from './components/PaymentRetryBanner';
import ProtectedRoute from './components/ProtectedRoute';
import FloatingCompanion from './components/FloatingCompanion';
import { VerseLensHost } from './components/VerseLensPanel';
import SermonFloatingButton from './components/SermonFloatingButton';
import TrialBanner from './components/TrialBanner';
import TrialWelcomeDialog from './components/TrialWelcomeDialog';
import PendingActionRunner from './components/PendingActionRunner';
import OpenDeepLink from './components/OpenDeepLink';
import EmbedBuilder from './components/EmbedBuilder';
import Factbook from './components/Factbook';
import PublicVerseReader from './components/PublicVerseReader';
import WidgetPage from './components/WidgetPage';
import PrayerPage from './components/PrayerPage';
import GuestUpgradeNudge from './components/GuestUpgradeNudge';
import MilestoneShareNudge from './components/MilestoneShareNudge';

// Tiny shell so EmbedBuilder gets a page background even when accessed standalone
const EmbedBuilderPage = () => (
  <div className="min-h-screen bg-slate-50 dark:bg-slate-950 py-6">
    <EmbedBuilder />
  </div>
);
import { Toaster } from './components/ui/sonner';
import InstallPwaBanner from './components/InstallPwaBanner';
import UpdateAvailableToast from './components/UpdateAvailableToast';
import LanguageOnboarding from './components/LanguageOnboarding';
import StandaloneToolPage from './components/StandaloneToolPage';
import StudyPathViewer from './components/StudyPathViewer';
import StudyPathsListPage from './components/StudyPathsListPage';
import SermonPrepTool from './components/SermonPrepTool';
import SermonPresentMode from './components/SermonPresentMode';
import PastorModePublicPage from './components/PastorModePublicPage';

// Sample verse page (public)
import Hero from './components/Hero';
import BibleReader from './components/BibleReader';

const SamplePage = () => {
  const sampleVerse = {
    book: 'John',
    chapter: 3,
    verse: 16,
    text: 'For God so loved the world, that he gave his only begotten Son, that whosoever believeth in him should not perish, but have everlasting life.',
    translation: 'KJV'
  };

  return (
    <div className="min-h-screen bg-white">
      <Hero onSearch={() => {}} />
      <div className="max-w-4xl mx-auto px-4 py-12">
        <BibleReader initialVerse={sampleVerse} onAIAnalyze={() => {}} isLoadingAI={false} />
        <div className="mt-8 text-center">
          <p className="text-slate-600 mb-4">
            Sign up to access full features: AI insights, multiple translations, study tools, and more!
          </p>
          <a href="/login" className="text-blue-600 hover:underline font-semibold">
            Get Started →
          </a>
        </div>
      </div>
    </div>
  );
};

// Router component to handle session_id
function AppRouter() {
  const location = useLocation();
  
  // Check URL hash for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  // Show floating companion on dashboard and bible pages
  const showCompanion = location.pathname === '/dashboard' || location.pathname.startsWith('/bible');
  
  return (
    <>
      <PaymentRetryBanner />
      <Routes>
        <Route path="/" element={<div className="min-h-screen bg-background"><NewLandingPage /></div>} />
        <Route path="/login" element={<div className="min-h-screen bg-background"><Login /></div>} />
        <Route path="/sample" element={<div className="min-h-screen bg-background"><SamplePage /></div>} />
        <Route path="/read" element={<PublicVerseReader />} />
        <Route path="/widget" element={<div className="min-h-screen bg-background"><WidgetPage /></div>} />
        <Route path="/explore" element={<div className="min-h-screen bg-background"><PublicExplorePage /></div>} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <div className="min-h-screen bg-background">
                <Dashboard />
              </div>
            </ProtectedRoute>
          }
        />
        <Route path="/bible" element={<BiblePage />} />
        {/* Shareable deep link: /bible/John/3 */}
        <Route path="/bible/:book/:chapter" element={<BiblePage />} />

        {/* Prayer List + Community Prayer Wall — public to VIEW; interacting prompts login */}
        <Route path="/prayer" element={<PrayerPage />} />

        {/* Universal deep-link entry — used by Slidecon, email shares, and any
            external app that wants to land users at a specific verse/topic/draft
            inside Lumera. Public — no auth required to LAND here; if a target
            page is protected the ProtectedRoute will intercept after redirect. */}
        <Route path="/open" element={<OpenDeepLink />} />

        {/* Public shared study path viewer — no auth gate so links are shareable. */}
        <Route path="/path/:id" element={<StudyPathViewer />} />
        <Route path="/study-paths" element={<ProtectedRoute><StudyPathsListPage /></ProtectedRoute>} />

        {/* Public embed-builder — pastors and bloggers grab a snippet
            without logging in. No auth gate. */}
        <Route path="/embed-builder" element={<EmbedBuilderPage />} />

        {/* Factbook — Logos-parity Phase 2: person/place/concept entries */}
        <Route path="/factbook" element={<ProtectedRoute><Factbook /></ProtectedRoute>} />

        {/* Community — public to VIEW (feed prompts sign-in, games/videos open); interacting prompts login */}
        <Route path="/community" element={<CommunityPage />} />

        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <SuperAdminCenter />
            </ProtectedRoute>
          }
        />

        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/pastor" element={<PastorModePublicPage />} />
        <Route path="/mock-payment" element={<MockPaymentPage />} />
        <Route path="/collab" element={<CollabPage />} />
        
        <Route
          path="/payment/success"
          element={
            <ProtectedRoute>
              <PaymentSuccessPage />
            </ProtectedRoute>
          }
        />

        {/* Standalone Tool Pages */}
        <Route path="/profile" element={<ProtectedRoute><StandaloneToolPage toolId="profile" /></ProtectedRoute>} />
        <Route path="/maps" element={<StandaloneToolPage toolId="maps" />} />
        <Route path="/atlas" element={<StandaloneToolPage toolId="atlas" />} />
        <Route path="/gospel-harmony" element={<ProtectedRoute><StandaloneToolPage toolId="harmony" /></ProtectedRoute>} />
        <Route path="/topical" element={<ProtectedRoute><StandaloneToolPage toolId="topical" /></ProtectedRoute>} />
        <Route path="/historic-plans" element={<ProtectedRoute><StandaloneToolPage toolId="historic-plans" /></ProtectedRoute>} />
        <Route path="/apocrypha" element={<ProtectedRoute><StandaloneToolPage toolId="apocrypha" /></ProtectedRoute>} />
        <Route path="/original-languages" element={<ProtectedRoute><StandaloneToolPage toolId="original-languages" /></ProtectedRoute>} />
        <Route path="/isbe" element={<ProtectedRoute><StandaloneToolPage toolId="isbe" /></ProtectedRoute>} />
        <Route path="/chronology" element={<ProtectedRoute><StandaloneToolPage toolId="chronology" /></ProtectedRoute>} />
        <Route path="/ai-insights" element={<ProtectedRoute><StandaloneToolPage toolId="ai" /></ProtectedRoute>} />
        <Route path="/word-study" element={<ProtectedRoute><StandaloneToolPage toolId="wordstudy" /></ProtectedRoute>} />
        <Route path="/concordance" element={<ProtectedRoute><StandaloneToolPage toolId="concordance" /></ProtectedRoute>} />
        <Route path="/commentary" element={<ProtectedRoute><StandaloneToolPage toolId="commentary" /></ProtectedRoute>} />
        <Route path="/notes" element={<ProtectedRoute><StandaloneToolPage toolId="notes" /></ProtectedRoute>} />
        <Route path="/dictionary" element={<ProtectedRoute><StandaloneToolPage toolId="dictionary" /></ProtectedRoute>} />
        <Route path="/characters" element={<ProtectedRoute><StandaloneToolPage toolId="characters" /></ProtectedRoute>} />
        <Route path="/devotional" element={<ProtectedRoute><StandaloneToolPage toolId="devotional" /></ProtectedRoute>} />
        <Route path="/bookmarks" element={<ProtectedRoute><StandaloneToolPage toolId="bookmarks" /></ProtectedRoute>} />
        <Route path="/cross-references" element={<ProtectedRoute><StandaloneToolPage toolId="crossrefs" /></ProtectedRoute>} />
        <Route path="/reading-plans" element={<ProtectedRoute><StandaloneToolPage toolId="reading-plans" /></ProtectedRoute>} />
        <Route path="/highlights" element={<ProtectedRoute><StandaloneToolPage toolId="highlights" /></ProtectedRoute>} />
        <Route path="/verse-image" element={<ProtectedRoute><StandaloneToolPage toolId="verse-image" /></ProtectedRoute>} />
        <Route path="/parallel" element={<ProtectedRoute><StandaloneToolPage toolId="parallel" /></ProtectedRoute>} />
        <Route path="/comparison" element={<ProtectedRoute><StandaloneToolPage toolId="comparison" /></ProtectedRoute>} />
        <Route path="/interlinear" element={<ProtectedRoute><StandaloneToolPage toolId="interlinear" /></ProtectedRoute>} />
        <Route path="/verse-of-the-day" element={<ProtectedRoute><StandaloneToolPage toolId="votd" /></ProtectedRoute>} />
        <Route path="/ai-companion" element={<ProtectedRoute><StandaloneToolPage toolId="companion" /></ProtectedRoute>} />
        <Route path="/companion" element={<ProtectedRoute><StandaloneToolPage toolId="companion" /></ProtectedRoute>} />
        <Route path="/videos" element={<StandaloneToolPage toolId="videos" />} />
        <Route path="/notifications" element={<ProtectedRoute><StandaloneToolPage toolId="notifications" /></ProtectedRoute>} />
        <Route path="/sermon-prep" element={<ProtectedRoute><SermonPrepTool /></ProtectedRoute>} />
        <Route path="/sermon-prep/present" element={<ProtectedRoute><SermonPresentMode /></ProtectedRoute>} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      
      {/* Floating AI Companion - shows on dashboard and bible pages */}
      {showCompanion && <FloatingCompanion />}

      {/* Verse Lens — global unified commentary aggregator. Listens to
          `lumera:verse-lens` custom events so any pane can open it. */}
      <VerseLensHost />

      {/* Global Sermon Prep shortcut */}
      <SermonFloatingButton />
      {/* Trial-ending nudge (shows only during an active free trial) */}
      <TrialBanner />
      <TrialWelcomeDialog />
      <PendingActionRunner />
      {/* Guest "save your progress" nudge (guests only, after real engagement) */}
      <GuestUpgradeNudge />
      {/* Milestone "share to your story" nudge (audio chapter / reading plan / sundae) */}
      <MilestoneShareNudge />
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <div className="App">
        <BrowserRouter>
          <ThemeProvider>
            <LanguageProvider>
              <AuthProvider>
                <Toaster position="top-center" expand visibleToasts={4} />
                <AppRouter />
                <LanguageOnboarding />
                <InstallPwaBanner />
                <UpdateAvailableToast />
              </AuthProvider>
            </LanguageProvider>
          </ThemeProvider>
        </BrowserRouter>
      </div>
    </ErrorBoundary>
  );
}

export default App;
